import { useState } from 'react'
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Lock, LogOut, Settings, Trash2, Users } from 'lucide-react'

import { PostCard } from '@/components/post-card'
import { PostComposer } from '@/components/post-composer'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import {
  useGroup,
  useGroupFeed,
  useGroupMembers,
  useDissolveGroup,
  useJoinGroup,
  useLeaveGroup,
  useUpdateGroup,
  type Group,
} from '@/features/sosial/api'
import { ApiError } from '@/lib/api'

const route = getRouteApi('/authenticated/app/grup/$slug')

const namaPeran: Record<string, string> = {
  owner: 'Pemilik',
  admin: 'Pengelola',
  member: 'Anggota',
}

export function GrupDetailPage() {
  const { slug } = route.useParams()
  const { data: grup, isLoading } = useGroup(slug)
  const { data: isi, error } = useGroupFeed(slug)
  const { data: anggota } = useGroupMembers(slug, Boolean(grup?.can_read))

  const gabung = useJoinGroup()
  const keluar = useLeaveGroup()
  const toast = useToast()
  const [sunting, setSunting] = useState(false)

  if (isLoading) return <Skeleton className="h-96 rounded-2xl" />

  if (!grup) {
    return (
      <Card>
        <EmptyState
          title="Grup tidak ditemukan"
          description="Grupnya mungkin sudah dihapus."
          action={
            <Link to="/app/grup">
              <Button variant="outline">Kembali ke daftar grup</Button>
            </Link>
          }
        />
      </Card>
    )
  }

  const anggotaGrup = Boolean(grup.my_role)
  const tertutupBagiSaya = !grup.can_read

  return (
    <div className="space-y-6">
      <Link
        to="/app/grup"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Semua grup
      </Link>

      <Card>
        <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-brand-soft text-brand">
            <Users className="size-7" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-extrabold tracking-tight text-ink">{grup.name}</h1>
              {grup.privacy === 'private' ? (
                <Badge tone="neutral" icon={<Lock className="size-3" />}>
                  Tertutup
                </Badge>
              ) : null}
              {grup.my_role ? <Badge tone="brand">{namaPeran[grup.my_role]}</Badge> : null}
            </div>

            {grup.description ? <p className="mt-1 text-muted">{grup.description}</p> : null}
            <p className="mono-label mt-1.5 text-[10px] text-subtle">
              {grup.member_count} anggota · {grup.post_count} kiriman
            </p>
          </div>

          <div className="flex shrink-0 gap-2">
            {grup.can_manage ? (
              <Button variant="outline" onClick={() => setSunting(true)}>
                <Settings className="size-4" />
                Kelola
              </Button>
            ) : null}

            {anggotaGrup ? (
              <Button
                variant="outline"
                disabled={keluar.isPending}
                onClick={() =>
                  keluar.mutate(slug, {
                    onSuccess: () => toast.success('Keluar dari grup'),
                    onError: (e) =>
                      toast.error('Gagal keluar', e instanceof ApiError ? e.message : undefined),
                  })
                }
              >
                <LogOut className="size-4" />
                Keluar
              </Button>
            ) : (
              <Button
                disabled={gabung.isPending}
                onClick={() =>
                  gabung.mutate(slug, {
                    onSuccess: () => toast.success('Bergabung ke grup'),
                    onError: (e) =>
                      toast.error('Gagal bergabung', e instanceof ApiError ? e.message : undefined),
                  })
                }
              >
                Gabung
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[1fr_17rem] [&>*]:min-w-0">
        <div className="space-y-4">
          {anggotaGrup ? (
            <PostComposer groupId={grup.id} placeholder={`Tulis sesuatu untuk ${grup.name}…`} />
          ) : null}

          {tertutupBagiSaya ? (
            <Card>
              <EmptyState
                icon={<Lock className="size-6" />}
                title="Grup ini tertutup"
                description="Bergabunglah dulu untuk membaca kirimannya. Grupnya sendiri tetap terlihat supaya Anda tahu grup ini ada."
                action={<Button onClick={() => gabung.mutate(slug)}>Gabung grup</Button>}
              />
            </Card>
          ) : error ? (
            <Card>
              <EmptyState
                title="Kiriman tidak dapat dimuat"
                description={error instanceof ApiError ? error.message : 'Coba muat ulang halaman ini.'}
              />
            </Card>
          ) : (isi?.posts ?? []).length === 0 ? (
            <Card>
              <EmptyState
                illustration="box"
                icon={<Users className="size-6" />}
                title="Belum ada kiriman"
                description={
                  anggotaGrup
                    ? 'Jadilah yang pertama menulis di grup ini.'
                    : 'Bergabunglah untuk ikut menulis di sini.'
                }
              />
            </Card>
          ) : (
            <div>
              {(isi?.posts ?? []).map((post) => (
                <PostCard key={post.id} post={post} tampilkanGrup={false} />
              ))}
            </div>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <Card>
            <CardHeader title="Anggota" description={`${grup.member_count} orang`} />
            <CardBody className="space-y-2.5">
              {!grup.can_read ? (
                <p className="text-sm text-muted">Daftar anggota terlihat setelah Anda bergabung.</p>
              ) : (
                (anggota?.members ?? []).slice(0, 12).map((a) => (
                  <div key={a.user.id} className="flex items-center gap-2.5">
                    <Avatar name={a.user.full_name} src={a.user.avatar_url} size="sm" />
                    <div className="min-w-0 flex-1">
                      <Link
                        to="/talenta/$expertId"
                        params={{ expertId: a.user.id }}
                        className="block truncate text-sm font-semibold text-ink hover:underline"
                      >
                        {a.user.full_name}
                      </Link>
                      {a.role !== 'member' ? (
                        <span className="mono-label text-[10px] text-brand">{namaPeran[a.role]}</span>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </CardBody>
          </Card>
        </aside>
      </div>

      <DialogKelolaGrup open={sunting} grup={grup} onClose={() => setSunting(false)} />
    </div>
  )
}

function DialogKelolaGrup({
  open,
  grup,
  onClose,
}: {
  open: boolean
  grup: Group
  onClose: () => void
}) {
  const [nama, setNama] = useState(grup.name)
  const [deskripsi, setDeskripsi] = useState(grup.description ?? '')
  const [privasi, setPrivasi] = useState(grup.privacy)
  const [galat, setGalat] = useState('')
  const [mintaBubar, setMintaBubar] = useState(false)

  const simpan = useUpdateGroup(grup.slug)
  const bubarkan = useDissolveGroup()
  const navigate = useNavigate()
  const toast = useToast()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Kelola grup"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Batal
          </Button>
          <Button
            disabled={simpan.isPending}
            onClick={() => {
              setGalat('')
              simpan.mutate(
                { name: nama, description: deskripsi, privacy: privasi },
                {
                  onSuccess: () => {
                    toast.success('Grup diperbarui')
                    onClose()
                  },
                  onError: (error) =>
                    setGalat(error instanceof ApiError ? error.message : 'Gagal menyimpan.'),
                },
              )
            }}
          >
            {simpan.isPending ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <p className="rounded-xl bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
            {galat}
          </p>
        ) : null}

        <Input label="Nama grup" required value={nama} onChange={(e) => setNama(e.target.value)} />
        <Textarea
          label="Keterangan"
          rows={3}
          value={deskripsi}
          onChange={(e) => setDeskripsi(e.target.value)}
        />
        <Select
          label="Keterbukaan"
          value={privasi}
          onChange={(e) => setPrivasi(e.target.value as 'public' | 'private')}
          hint="Mengubahnya berlaku seketika bagi seluruh kiriman yang sudah ada."
        >
          <option value="public">Terbuka</option>
          <option value="private">Tertutup</option>
        </Select>

        {/*
          Membubarkan diletakkan di bawah, di balik satu langkah tambahan, dan
          hanya bagi pemiliknya. Tindakan yang menutup kiriman seluruh anggota
          sekaligus tidak pantas bersebelahan dengan tombol simpan.
        */}
        {grup.can_dissolve ? (
          <div className="border-t border-line pt-4">
            {mintaBubar ? (
              <div className="space-y-3 rounded-xl bg-danger-soft p-3">
                <p className="text-sm text-danger">
                  Bubarkan <strong>{grup.name}</strong>? {grup.post_count} kiriman di dalamnya ikut
                  hilang bagi seluruh {grup.member_count} anggota, dan ini tidak dapat dibatalkan.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setMintaBubar(false)}>
                    Batal
                  </Button>
                  <Button
                    variant="danger"
                    disabled={bubarkan.isPending}
                    onClick={() =>
                      bubarkan.mutate(grup.slug, {
                        onSuccess: () => {
                          toast.success('Grup dibubarkan')
                          onClose()
                          navigate({ to: '/app/grup' })
                        },
                        onError: (error) =>
                          setGalat(
                            error instanceof ApiError ? error.message : 'Gagal membubarkan grup.',
                          ),
                      })
                    }
                  >
                    {bubarkan.isPending ? 'Membubarkan…' : 'Ya, bubarkan'}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setMintaBubar(true)}
                className="flex items-center gap-2 text-sm font-medium text-danger hover:underline"
              >
                <Trash2 className="size-4" />
                Bubarkan grup ini
              </button>
            )}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
